// ====================================================================
// OCCLUSION FADE — segment math + tuning for the "ghost" translucency
// ====================================================================
// The Diorama camera sits high and still and never dollies in (that hard
// pull-in was the "swing"). So when a tree canopy or a hut crosses the line
// between the camera and Chocky, we keep the camera put and fade the OCCLUDER
// to translucent instead. This module is the pure-math/policy half: it owns the
// camera->Chocky segment test and the tuning numbers. The GPU plumbing lives
// where the data is — per-instance alpha in world/chunks.js (it alone sees the
// streamed InstancedMeshes), per-mesh opacity for merged structures in main.js.
// No THREE import on purpose: this is trivially testable scalar math.

// Hard kill switch. Left on everywhere (including LOW) because the diorama has
// no pull-in fallback, so disabling fade would leave occluders solidly blocking
// Chocky. Flip true only if profiling shows the transparent pass hurts on a
// specific device; the caller may also THROTTLE the cadence on LOW instead.
export const OCCLUSION_FADE_DISABLED = false;

export const FADE_MIN = 0.22;          // ghosted opacity (low enough to see through)
export const FADE_K = 6;               // ease rate (1/s); ~0.3s in/out, smooth not snappy
// Small grace around each prop's true visual radius before it counts as blocking
// Chocky's sightline. The caller combines this with the instance scale/type
// radius, so tiny trunks do not ghost just because they are near the ray.
export const FADE_INSTANCE_MARGIN = 0.16;

// Squared distance from point P to the segment AB, with the projection clamped
// to [0,1] so only things actually BETWEEN the camera and Chocky register —
// props off to the side or well past either end fall outside the radius.
// Pure scalars in/out: allocates nothing, safe to call per instance per frame.
export function segDistSq(ax, ay, az, bx, by, bz, px, py, pz) {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const apx = px - ax, apy = py - ay, apz = pz - az;
  const abLenSq = abx * abx + aby * aby + abz * abz;
  let t = abLenSq > 1e-9 ? (apx * abx + apy * aby + apz * abz) / abLenSq : 0;
  if (t < 0) t = 0; else if (t > 1) t = 1;
  const dx = apx - abx * t, dy = apy - aby * t, dz = apz - abz * t;
  return dx * dx + dy * dy + dz * dz;
}

// Frame-rate-independent ease of `cur` toward `target` at rate FADE_K. Returns
// the new value; caller decides whether it changed enough to upload.
export function easeFade(cur, target, dt) {
  return cur + (target - cur) * (1 - Math.exp(-FADE_K * dt));
}
